import {
  ApiError,
  Auth0AccountRole,
  CustomDataTypeKeyResponse,
  ManageCrmService,
  OrganizationDataKeyResponse,
  OrganizationResponseForAdmins,
} from "client/openapi";
import { useEffect, useState } from "react";
import Notifications from "util/notifications";
import { APIResponse, PageStatus } from "types";

import CustomTypes from "./CustomTypes";
import DataFields from "./UserData";

type SetKeys = (keys: OrganizationDataKeyResponse[]) => void;

/**
 * The field lists belong to the CRM page, so a field added, changed or
 * deleted here reaches Visualize and Search without a reload.
 */
export default function CRMdata({
  organization,
  studentKeys,
  setStudentKeys,
  parentKeys,
  setParentKeys,
  tutorKeys,
  setTutorKeys,
}: {
  organization: OrganizationResponseForAdmins;
  studentKeys: OrganizationDataKeyResponse[];
  setStudentKeys: SetKeys;
  parentKeys: OrganizationDataKeyResponse[];
  setParentKeys: SetKeys;
  tutorKeys: OrganizationDataKeyResponse[];
  setTutorKeys: SetKeys;
}) {
  const [customKeys, setCustomKeys] = useState<CustomDataTypeKeyResponse[]>([]);
  const [, setStatus] = useState<PageStatus>(PageStatus.LOADING);
  const [error, setError] = useState<APIResponse>();
  const [isLoadingCustomKeys, setIsLoadingCustomKeys] = useState(true);

  async function getCustomDataTypes(orgId: number) {
    setIsLoadingCustomKeys(true);
    setStatus(PageStatus.LOADING);

    await ManageCrmService.getCustomDataTypes({ orgId })
      .then((keys) => {
        setCustomKeys(keys);
        setIsLoadingCustomKeys(false);
        setStatus(PageStatus.SUCCESS);
      })
      .catch((e: ApiError) => {
        setIsLoadingCustomKeys(false);
        setStatus(PageStatus.ERROR);
        setError({ message: "An unexpected error occurred" });
        console.error(`Error (#${e.status}): ${e.message}`);
      });
  }

  // The field lists come from the CRM page; only the custom types load here.
  useEffect(() => {
    getCustomDataTypes(organization.id);
  }, [organization.id]);

  useEffect(() => {
    if (error?.message) {
      Notifications.error(error.message);
    }
  }, [error]);

  if (isLoadingCustomKeys) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-900"></div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: "150px" }}>
      <div>
        <DataFields
          organization={organization}
          keys={studentKeys}
          remainingKeysRequiredForBulkImport={parentKeys}
          setKeys={setStudentKeys}
          setStatus={setStatus}
          setError={setError}
          userType={Auth0AccountRole.ME}
        />
      </div>
      <div className="mt-10">
        <DataFields
          organization={organization}
          keys={parentKeys}
          remainingKeysRequiredForBulkImport={studentKeys}
          setKeys={setParentKeys}
          setStatus={setStatus}
          setError={setError}
          userType={Auth0AccountRole.PARENT}
        />
      </div>
      <div className="mt-10">
        <DataFields
          organization={organization}
          keys={tutorKeys}
          remainingKeysRequiredForBulkImport={[]}
          setKeys={setTutorKeys}
          setStatus={setStatus}
          setError={setError}
          userType={Auth0AccountRole.ORG_TUTOR}
        />
      </div>
      <div className="mt-10">
        <CustomTypes
          organization={organization}
          keys={customKeys}
          setKeys={setCustomKeys}
          setStatus={setStatus}
          setError={setError}
        />
      </div>
    </div>
  );
}
